import { useState } from "react";
import { useEffect } from "react";

import { Box, Slider, useTheme } from "@mui/material";
import Typography from "@mui/material/Typography";

const SymptomScale = props => {
  const {
    defaultValue = 0.5,
    defaultLabel = "Choose Range",
    valueEditedHandler = null,
    isDisabled = false,
    valueIndex = 0,
  } = props;

  const valuetext = value => {
    return (
      <Typography id="discrete-slider" variant="h6" gutterBottom>
        {`${value * 10}`}
      </Typography>
    );
  };

  const calculateDefaultValue = () => {
    try {
      let initialValue = parseFloat(defaultValue);
      return Math.round(initialValue);
    } catch (error) {
      console.log(error);
      return 0.5;
    }
  };

  const [sliderValue, setSliderValue] = useState(calculateDefaultValue);
  const [initialValue] = useState(sliderValue);

  const getMarks = () => {
    const marks = [
      {
        value: 0,
        label: "0",
      },
      { value: initialValue, label: initialValue },
      {
        value: 10,
        label: "10",
      },
    ];

    return marks;
  };

  const handleSliderChange = (event, newValue) => {
    if (valueEditedHandler != null) {
      valueEditedHandler(newValue, valueIndex);
    }
    setSliderValue(newValue);
  };

  const [sliderLable, setSliderLabel] = useState(defaultLabel);

  const getLabel = (theLabel, value) => {
    if (theLabel === "Choose Range") {
      return theLabel;
    }

    if (value <= 3) {
      return theLabel + " (Not Much)";
    } else if (value >= 7) {
      return theLabel + " (A Lot)";
    } else {
      return theLabel + " (Some)";
    }
  };

  // Check given name
  useEffect(() => {
    setSliderLabel(getLabel(defaultLabel, sliderValue));
  }, [sliderValue, defaultLabel]);

  const theme = useTheme();

  return (
    <Box
      sx={[
        {
          webkitUserSelect: "none",
          userSelect: "none",
          width: "100%",
          overflow: "hidden",
          background: theme.palette.foreground.main,
          color: theme.palette.foreground.contrastText,
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
          borderRadius: theme.spacing(1),
          border: `2px solid ${theme.palette.foreground.main}`,
        },
        sliderValue >= 7 && {
          border: `2px solid ${theme.palette.error.main}`,
        },
        sliderValue > 3 &&
          sliderValue < 7 && {
            border: `2px solid ${theme.palette.warning.main}`,
          },
        sliderValue <= 3 && {
          border: `2px solid ${theme.palette.success.main}`,
        },
      ]}
    >
      <Typography
        id="discrete-slider"
        variant="h6"
        sx={{
          webkitUserSelect: "none",
          userSelect: "none",
          textAlign: "left",
          marginTop: theme.spacing(1),
        }}
      >
        {sliderLable}
      </Typography>
      <Box sx={{ marginLeft: theme.spacing(2), marginRight: theme.spacing(2) }}>
        <Slider
          defaultValue={initialValue}
          value={sliderValue}
          onChange={handleSliderChange}
          getAriaValueText={valuetext}
          aria-labelledby="discrete-slider"
          valueLabelDisplay="auto"
          step={1}
          min={0}
          max={10}
          marks={getMarks()}
          disabled={isDisabled}
          size="medium "
          sx={[
            {
              height: 2,
              width: `100%`,
              boxSizing: "content-box",
              padding: "13px 0",
              display: "inline-block",
              position: "relative",
              cursor: "pointer",
              touchAction: "none",
              color: theme.palette.primary.main,
              WebkitTapHighlightColor: "transparent",
              "& .Mui-disabled": {
                pointerEvents: "none",
                cursor: "default",
                color: theme.palette.grey[400],
              },
              "@media (pointer: coarse)": {
                padding: "20px 0",
              },
              "@media print": {
                colorAdjust: "exact",
              },
              "& .MuiSlider-markLabel": {
                ...theme.typography.subtitle1,
                fontWeight: 600,
                position: "absolute",
                marginTop: `-${theme.spacing(1)}`,
                marginLeft: "4px",
                whiteSpace: "nowrap",
                "@media (pointer: coarse)": {
                  top: 40,
                  "$vertical &": {
                    left: 31,
                  },
                },
              },
              "& .MuiSlider-mark": {
                height: 8,
                width: 8,
                borderRadius: "50%",
                opacity: 1,
              },
              "& .MuiSlider-thumb": {
                marginLeft: "4px",
              },
              "& .MuiSlider-valueLabel": {
                fontSize: 12,
                padding: 0,
                width: 32,
                height: 32,
                marginTop: theme.spacing(1),
                borderRadius: "50% 50% 50% 0",
                transformOrigin: "bottom left",
                transform: "translate(50%, -100%) rotate(-45deg) scale(0)",
                "&:before": { display: "none" },
                "&.MuiSlider-valueLabelOpen": {
                  transform: "translate(50%, -100%) rotate(-45deg) scale(1)",
                },
                "& > *": {
                  transform: "rotate(45deg)",
                },
              },
            },
            sliderValue >= 7 && {
              "& .MuiSlider-track": {
                color: theme.palette.error.main,
              },
              "& .MuiSlider-rail, .MuiSlider-mark, .MuiSlider-markActive, .MuiSlider-thumb, .MuiSlider-valueLabel":
                {
                  backgroundColor: theme.palette.error.main,
                },
            },
            sliderValue > 3 &&
              sliderValue < 7 && {
                "& .MuiSlider-track": {
                  color: theme.palette.warning.main,
                },
                "& .MuiSlider-rail, .MuiSlider-mark, .MuiSlider-markActive, .MuiSlider-thumb, .MuiSlider-valueLabel":
                  {
                    backgroundColor: theme.palette.warning.main,
                  },
              },
            sliderValue <= 3 && {
              "& .MuiSlider-track": {
                color: theme.palette.success.main,
              },
              "& .MuiSlider-rail, .MuiSlider-mark, .MuiSlider-markActive, .MuiSlider-thumb,.MuiSlider-valueLabel":
                {
                  backgroundColor: theme.palette.success.main,
                },
            },
          ]}
        />
      </Box>
    </Box>
  );
};

export default SymptomScale;
